import TimeConstants from './dateValues/TimeConstants';
import { asDate, compare, OwaDate, today as dateTimeToday } from 'owa-datetime';
import { DayOfWeek, DAYS__TO_MASK_MAP } from './dateValues/DateValues';

export function today(date?: Date): Date {
    return date || asDate(dateTimeToday());
}

/**
 * Returns start of the day for the given date
 */
export function startOfDay(passedDate: Date): Date {
    const date = new Date(passedDate.toDateString());
    return date;
}

/**
 * Adds the given number of days to the input date
 */
export function addDays(date: Date, countOfDay: number): Date {
    const passedDate = new Date(date.getTime());
    return new Date(passedDate.setDate(passedDate.getDate() + countOfDay));
}

/**
 * Determines the number of days the end date is away from the start and returns 0 if the end date is smaller than the start date
 * @param startDate
 * @param endDate
 */
export function differenceInDays(startDate: Date, endDate: Date): number {
    const endTime = endDate.getTime();
    const startTime = startDate.getTime();

    return startTime > endTime
        ? 0
        : Math.floor((endTime - startTime) / TimeConstants.MillisecondsInOneDay);
}

/**
 * Determines the number of minutes the end date is away from the start and returns 0 if the end date is smaller than the start date
 * @param startDate
 * @param endDate
 */
export function differenceInMinutes(startDate: Date, endDate: Date): number {
    const endTime = endDate.getTime();
    const startTime = startDate.getTime();

    return startTime > endTime
        ? 0
        : Math.floor((endTime - startTime) / TimeConstants.MillisecondsIn1Min);
}

/**
 * Returns true if the taget date is before the given passed date
 */
export function isBefore(targetDate: Date, passedDate: Date): boolean {
    return targetDate.getTime() < passedDate.getTime();
}

/**
 * Returns true if the input date is today
 */
export function isToday(date: Date, todayDate?: Date): boolean {
    return startOfDay(date).getTime() === today(todayDate).getTime();
}

/**
 * Returns the earlier of the two dates
 * @param date1
 * @param date2
 */
export const getEarlierDate = (date1: OwaDate, date2: OwaDate): OwaDate =>
    compare(date1, date2) === -1 ? date1 : date2;

/**
 * Returns the later one of the two dates
 * @param date1
 * @param date2
 */
export const getLaterDate = (date1: OwaDate, date2: OwaDate): OwaDate =>
    compare(date1, date2) === 1 ? date1 : date2;

/**
 * Returns true if the input date is tomorrow
 */
export function isTomorrow(date: Date, todayDate?: Date): boolean {
    const currentDate = today(todayDate);
    return startOfDay(date).getTime() === addDays(currentDate, 1).getTime();
}

/**
 * Returns true if the input date is yesterday
 */
export function isYesterday(passedDate: Date, todayDate?: Date): boolean {
    const currentDate = today(todayDate);
    return startOfDay(passedDate).getTime() === addDays(currentDate, -1).getTime();
}

/**
 * Returns true if the input date belongs to current year
 */
export function isCurrentYear(date: Date, todayDate?: Date): boolean {
    return isSameYear(date, today(todayDate));
}

/**
 * Function deterimines if both the dates are in the same year
 * @param passedDate
 * @param targetDate
 */
export function isSameYear(passedDate: Date, targetDate: Date): boolean {
    return passedDate.getFullYear() === targetDate.getFullYear();
}

/**
 * Returns true if both the passed dates belong to the same month in the same year
 * @param passedDate
 * @param targetDate
 */
export function isSameMonth(passedDate: Date, targetDate: Date): boolean {
    return isSameYear(passedDate, targetDate) && passedDate.getMonth() === targetDate.getMonth();
}

/**
 * Returns true if the passed date represents the beginning of a month
 * @param passedDate
 */
export function isStartOfMonth(passedDate: Date): boolean {
    const startOfPassedDate: Date = startOfDay(passedDate);
    const previousDate: Date = addDays(startOfPassedDate, -1);

    return !isSameMonth(previousDate, startOfPassedDate);
}

/**
 * Converts a bit mask of days to a day of week array. Assumes that the bitmask has the lowest
 * order bit (0th bit) correspond to sunday, and go upwards from there. Format sat|fri|thurs|wed|tues|mon|sun
 *
 * for example, passing in 22 = 0010110, which results in [mon, tues, thurs]
 * @param workDays The bitmask for the work week days
 */
export function createDaysSetFromBitMask(daysMask: number): Set<DayOfWeek> {
    const result: Set<DayOfWeek> = new Set<DayOfWeek>();
    let mask: number = daysMask;

    while (mask) {
        // while days are remaining
        // get the rightmost bit
        const day: DayOfWeek = DAYS__TO_MASK_MAP[mask & -mask];
        result.add(day);
        // set the rightmost bit
        mask = mask & (mask - 1);
    }

    return result;
}
